/**
 * Sales-order approval "facts" — the catalog of fields an Approval step's
 * `appliesWhen` condition (and the split-rule builder) can reference, and the
 * shape of the fact record the workflow evaluates conditions against.
 *
 * Facts are computed once by the Compute Order Facts step (stamped onto the
 * draft lines) and aggregated into a per-order record at evaluation time.
 * Operators are the generic rule-evaluator set (see `ruleEvaluator.ts`). Boolean
 * facts are exposed as COUNTS (e.g. `order.listPriceMismatchCount`) so a numeric
 * `gt 0` condition works cleanly with the free-form rule builder rather than
 * relying on boolean string coercion.
 */

export type ApprovalFactOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "in"
  | "not_in"
  | "is_null"
  | "is_not_null";

export interface ApprovalFactField {
  /** Dot-path key into the fact record (e.g. "order.total"). */
  key: string;
  label: string;
  dataType: "number" | "string" | "boolean" | "enum" | "string_array";
  operators: ApprovalFactOperator[];
  enumValues?: string[];
  description?: string;
}

const NUMBER_OPS: ApprovalFactOperator[] = [
  "gte",
  "gt",
  "lte",
  "lt",
  "eq",
  "neq",
];
const STRING_OPS: ApprovalFactOperator[] = ["eq", "neq", "in", "not_in"];
const ARRAY_OPS: ApprovalFactOperator[] = ["contains", "not_contains"];

/**
 * The catalog powering the approval/split condition builders. Keep keys in sync
 * with `buildSalesOrderFactRecord` on the backend.
 */
export const SALES_ORDER_APPROVAL_FIELDS: ApprovalFactField[] = [
  {
    key: "order.total",
    label: "Order total",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Sum of line amounts (qty × unit price when amount is absent).",
  },
  {
    key: "order.lineCount",
    label: "Line count",
    dataType: "number",
    operators: NUMBER_OPS,
  },
  {
    key: "order.currency",
    label: "Currency",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "order.listPriceMismatchCount",
    label: "# lines with an unapproved price below list",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Lines whose unit price is below list with no approved discount — use `gt 0` to gate on any mismatch.",
  },
  {
    key: "order.noListPriceCount",
    label: "# lines with no list price",
    dataType: "number",
    operators: NUMBER_OPS,
    description: "Lines whose product has no list price in the ERP.",
  },
  {
    key: "order.insufficientInventoryCount",
    label: "# lines with insufficient inventory",
    dataType: "number",
    operators: NUMBER_OPS,
  },
  {
    key: "order.productGroups",
    label: "Product groups present",
    dataType: "string_array",
    operators: ARRAY_OPS,
    description: "Use `contains <group>` to gate on a product group.",
  },
  {
    key: "order.paymentTermsDays",
    label: "Payment terms (days)",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      'Credit/payment terms parsed from the PO text ("60Days", "Net 60", "2/10 net 30" → 30). Absent when the PO states none — use `gt 60` to gate on long credit terms.',
  },
  {
    key: "order.paymentTerms",
    label: "Payment terms (text)",
    dataType: "string",
    operators: STRING_OPS,
    description: "The payment terms exactly as extracted from the PO.",
  },
  {
    key: "order.shippingTerms",
    label: "Shipping terms (Incoterm)",
    dataType: "string",
    operators: STRING_OPS,
    description:
      "Incoterm code recognized from the PO's shipping terms (EXW, FOB, CIF, DAP, DDP, …); otherwise the extracted text upper-cased.",
  },
  {
    key: "customer.tier",
    label: "Customer tier",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "customer.type",
    label: "Customer type",
    dataType: "string",
    operators: STRING_OPS,
  },
];

/**
 * Per-LINE fields a split rule can reference. A split rule that matches a line
 * routes it into a separate draft (lines matching no rule stay on the original).
 * Reproduces the legacy ship-from-location strategy declaratively, e.g.
 * `variant.activeInZhuhai eq ORDERABLE` → a "China" split.
 *
 * `order.*` facts are computed once per draft and shared by every line, so a
 * rule can depend on the rest of the order — e.g. `variant.activeInZhuhai eq
 * ORDERABLE` AND `order.zhuhaiEquipmentLineCount gt 0` splits Zhuhai-orderable
 * lines out only when the order also carries Zhuhai equipment (parts follow the
 * equipment they ship with; a parts-only order never splits).
 */
export const SALES_ORDER_SPLIT_LINE_FIELDS: ApprovalFactField[] = [
  {
    key: "line.origin",
    label: "Line origin",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.warehouse",
    label: "Line warehouse",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.sku",
    label: "Line SKU",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.productGroup",
    label: "Line product group",
    dataType: "string",
    operators: STRING_OPS,
  },
  {
    key: "line.inventoryStatus",
    label: "Line inventory status",
    dataType: "enum",
    operators: STRING_OPS,
    enumValues: ["SUFFICIENT", "INSUFFICIENT", "UNKNOWN", "INACTIVE"],
  },
  {
    key: "variant.activeInZhuhai",
    label: "Variant active in Zhuhai",
    dataType: "string",
    operators: STRING_OPS,
    description: 'e.g. "ORDERABLE" for China-orderable products.',
  },
  {
    key: "variant.zhuhaiEligible",
    label: "Variant Zhuhai eligible",
    dataType: "string",
    operators: STRING_OPS,
    description:
      'ACTIVE_IN_ZHUHAI after the Oracle EBS "zhuhaiExclusions" map (PRODVIEW prefixes that never ship from Zhuhai): "EXCLUDED" for an excluded product, else the raw flag (e.g. "ORDERABLE").',
  },
  {
    key: "variant.prodview",
    label: "Variant product view",
    dataType: "string",
    operators: [...STRING_OPS, ...ARRAY_OPS],
    description:
      'Oracle PRODVIEW hierarchy (e.g. "SUPPLIES.TTO.RIBBONS.SUTTO..."); contains / not contains match a substring.',
  },
  {
    key: "order.equipmentLineCount",
    label: "Order equipment line count",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Sellable lines on the whole order whose product is EQUIPMENT-class (PRODVIEW).",
  },
  {
    key: "order.zhuhaiEquipmentLineCount",
    label: "Order Zhuhai equipment line count",
    dataType: "number",
    operators: NUMBER_OPS,
    description:
      "Equipment lines on the whole order whose product is also ACTIVE_IN_ZHUHAI = ORDERABLE.",
  },
];
